import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarBlank, Star, CaretDown, NotePencil, Table, PencilSimple, ArrowSquareOut, Plus, BellRinging } from "@phosphor-icons/react";
import api from "../lib/api";
import { formatCaptureDate } from "../lib/formatDate";
import { DingtalkLinksDialog } from "./DingtalkLinksDialog";
import { Checkbox } from "./ui/checkbox";
import { GlobalNoteBox } from "./GlobalNoteBox";

// Default preview cap - "查看所有" expands the rest in place (same page, no
// navigation - mirrors the completed-tasks toggle on TasksPage itself).
// Callers can override via `previewSize` (e.g. SchedulePage's week view
// shows more at once since it sits next to the equally-tall unscheduled
// panel).
const OVERDUE_PREVIEW_SIZE = 10;

// Cross-module overdue list: reuses the same `overdueTasks` bucket
// TasksPage/SchedulePage already compute via useTaskReminders (incomplete
// tasks whose due_date is before today), so a task shows up here even if
// it's scheduled (and therefore hidden from the plain backlog list above).
// Sorted oldest-due-date-first (most overdue at the top). Title-only rows
// (no date shown) so it matches the plain-title look of the "待安排任务"
// list it sits next to on the Schedule page; each row is draggable onto a
// calendar day cell there too (harmless no-op on TasksPage, which has no
// drop target).
export function OverdueTasksCard({ overdueTasks, onToggle, onOpenDetail, previewSize = OVERDUE_PREVIEW_SIZE, className }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const sorted = [...overdueTasks].sort((a, b) => a.due_date.localeCompare(b.due_date));
  const visible = showAll ? sorted : sorted.slice(0, previewSize);
  const hasMore = sorted.length > previewSize;

  return (
    <div className={`border border-red-500/40 bg-red-500/5 p-4 space-y-3 ${className || ""}`} data-testid="overdue-tasks-widget">
      <p className="font-mono-tag text-xs md:text-sm font-bold flex items-center gap-1.5 text-red-600">
        <BellRinging size={15} weight="fill" /> {t("taskSidebar.overdueTasksTitle")}
      </p>
      {sorted.length === 0 ? (
        <p className="text-xs font-bold text-muted-foreground py-6 text-center" data-testid="overdue-tasks-empty">
          {t("taskSidebar.overdueTasksEmpty")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="overdue-tasks-list">
          {visible.map((task) => (
            <div
              key={task.id}
              data-testid={`overdue-task-item-${task.id}`}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
              onClick={() => onOpenDetail(task)}
              className="flex items-center gap-2 border border-border bg-background px-2.5 py-2 cursor-grab hover:bg-secondary/40 transition-colors"
            >
              <Checkbox
                data-testid={`overdue-task-checkbox-${task.id}`}
                checked={false}
                onCheckedChange={() => onToggle(task)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="min-w-0 flex-1 text-xs md:text-sm font-bold truncate">{task.title}</span>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          data-testid="overdue-tasks-toggle-button"
          onClick={() => setShowAll((prev) => !prev)}
          className="flex items-center gap-1 text-xs md:text-sm font-bold text-red-600 hover:underline"
        >
          {showAll ? t("taskSidebar.collapseOverdueTasks") : t("taskSidebar.viewAllOverdueTasks")}
          <CaretDown size={12} weight="bold" className={showAll ? "rotate-180" : ""} />
        </button>
      )}
    </div>
  );
}

// Today's timed tasks in chronological order, read from the same `tasks`
// list TasksPage already loads (no extra API call needed). Capped at 5
// items by default; "查看完整日程" expands the rest in place (same page, no
// navigation - mirrors the overdue-tasks/completed-tasks toggle pattern).
// Clicking a task's title opens the same TaskDetailDialog the main task
// list/calendar use, so this widget doubles as a quick-edit surface.
function TodayScheduleCard({ tasks, onCreate, onOpenDetail, className }) {
  const { t, i18n } = useTranslation();
  const [newTitle, setNewTitle] = useState("");
  const [newTime, setNewTime] = useState("");
  const [showAll, setShowAll] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayAll = tasks
    .filter((tk) => tk.due_date === todayStr && tk.due_time && !tk.completed)
    .sort((a, b) => a.due_time.localeCompare(b.due_time));
  const todayItems = showAll ? todayAll : todayAll.slice(0, 5);
  const hasMore = todayAll.length > 5;
  const dateLabel = new Date().toLocaleDateString(i18n.language === "zh" ? "zh-CN" : "en-US", {
    year: "numeric", month: "2-digit", day: "2-digit", weekday: "long",
  });

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newTime) return;
    onCreate({ title: newTitle, due_time: newTime });
    setNewTitle("");
    setNewTime("");
  };

  return (
    <div className={`border border-border bg-card p-2.5 space-y-2 ${className || ""}`} data-testid="today-schedule-widget">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono-tag text-xs md:text-sm font-bold flex items-center gap-1.5 shrink-0">
          <CalendarBlank size={15} /> {t("taskSidebar.todayScheduleTitle")}
        </p>
        <span className="text-[11px] text-muted-foreground truncate">{dateLabel}</span>
      </div>

      {/* Quick-add: date is always today (implicit, not shown as a field) -
          this widget only surfaces same-day timed items, so a time is
          required to know where in the timeline it belongs. */}
      <form onSubmit={handleAdd} className="flex items-center gap-1.5" data-testid="today-schedule-add-form">
        <input
          data-testid="today-schedule-add-title-input"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder={t("taskSidebar.quickAddPlaceholder")}
          className="flex-1 min-w-0 border border-border bg-background px-2 py-1.5 text-xs font-bold outline-none focus:ring-1 focus:ring-primary/50"
        />
        <input
          data-testid="today-schedule-add-time-input"
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          className="w-[88px] shrink-0 border border-border bg-background px-1.5 py-1.5 text-xs font-bold outline-none focus:ring-1 focus:ring-primary/50"
        />
        <button
          type="submit"
          data-testid="today-schedule-add-submit-button"
          className="shrink-0 border border-border bg-primary text-primary-foreground p-1.5 hover:opacity-90 disabled:opacity-40"
          disabled={!newTitle.trim() || !newTime}
        >
          <Plus size={14} weight="bold" />
        </button>
      </form>

      {todayItems.length === 0 ? (
        <p className="text-xs font-bold text-muted-foreground py-6 text-center" data-testid="today-schedule-empty">
          {t("taskSidebar.todayScheduleEmpty")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="today-schedule-list">
          {todayItems.map((task) => (
            <div key={task.id} className="flex gap-2" data-testid={`today-schedule-item-${task.id}`}>
              <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
              <div className="min-w-0 flex-1">
                <p className="font-mono-tag text-xs font-bold text-primary">{task.due_time}</p>
                <p
                  data-testid={`today-schedule-title-${task.id}`}
                  onClick={() => onOpenDetail(task)}
                  className="text-sm font-bold truncate cursor-pointer hover:text-primary hover:underline"
                >
                  {task.title}
                </p>
                {task.description && <p className="text-xs text-muted-foreground truncate">{task.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          data-testid="view-full-schedule-link"
          onClick={() => setShowAll((prev) => !prev)}
          className="flex items-center gap-1 text-xs md:text-sm font-bold text-primary hover:underline"
        >
          {showAll ? t("taskSidebar.collapseSchedule") : t("taskSidebar.viewFullSchedule")}
          <CaretDown size={12} weight="bold" className={showAll ? "rotate-180" : ""} />
        </button>
      )}
    </div>
  );
}

// Independently fetches the "我的收藏" system tag + its captures (TasksPage
// has no reason to load capture/tag data otherwise). `refreshKey` is bumped
// by the parent whenever the capture-edit dialog it opens gets closed, so
// this list picks up title/date changes (or removal) without a full reload.
// Shows the first 4 by default; "查看全部笔记" expands the rest in place
// (same page, no navigation) - the full list is already fetched, so no
// extra request is needed to expand.
export function FavoriteNotesCard({ onOpenCapture, refreshKey }) {
  const { t, i18n } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: tags } = await api.get("/tags");
        const favTag = tags.find((tg) => tg.is_favorites);
        if (favTag) {
          const { data: captures } = await api.get("/captures", { params: { tag_id: favTag.id, sort_by: "updated_at" } });
          setFavorites(captures);
        }
      } catch (e) {
        // Supplementary widget, not a critical path - fail silently.
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  const visibleFavorites = showAll ? favorites : favorites.slice(0, 4);
  const hasMore = favorites.length > 4;

  return (
    <div className="border border-border bg-card p-2.5 space-y-2" data-testid="favorite-notes-widget">
      <p className="font-mono-tag text-xs md:text-sm font-bold flex items-center gap-1.5">
        <Star size={15} weight="fill" className="text-amber-500" /> {t("taskSidebar.favoriteNotesTitle")}
      </p>
      {!loading && favorites.length === 0 ? (
        <p className="text-xs font-bold text-muted-foreground py-6 text-center" data-testid="favorite-notes-empty">
          {t("taskSidebar.favoriteNotesEmpty")}
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="favorite-notes-list">
          {visibleFavorites.map((cap) => (
            <button
              key={cap.id}
              data-testid={`favorite-note-item-${cap.id}`}
              onClick={() => onOpenCapture(cap.id)}
              className="w-full flex items-center gap-2 border border-border bg-background px-2 py-1.5 text-left hover:ring-1 hover:ring-primary/40 transition-shadow"
            >
              <NotePencil size={15} className="text-muted-foreground shrink-0" />
              <span className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-bold truncate">{cap.title || t("captureCard.untitledFallback")}</p>
                <p className="text-[11px] text-muted-foreground">{t("captureCard.updatedAt")} {formatCaptureDate(cap.updated_at, i18n.language)}</p>
              </span>
              <Star size={12} weight="fill" className="text-amber-500 shrink-0" />
            </button>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          data-testid="view-all-notes-link"
          onClick={() => setShowAll((prev) => !prev)}
          className="flex items-center gap-1 text-xs md:text-sm font-bold text-primary hover:underline"
        >
          {showAll ? t("taskSidebar.collapseFavoriteNotes") : t("taskSidebar.viewAllNotes")}
          <CaretDown size={12} weight="bold" className={showAll ? "rotate-180" : ""} />
        </button>
      )}
    </div>
  );
}

// A manually-maintained bookmark list of DingTalk sheet links (not a live
// DingTalk API connection - see PRD 2026-07-31). Shows the first 4 with a
// quick open/edit action each; "查看全部表格" expands the rest in place (same
// page, no navigation - the full list is already fetched). Full add/edit/
// delete still lives in DingtalkLinksDialog, opened via the "+" button or
// a row's quick-edit button.
export function DingtalkLinksCard({ className }) {
  const { t, i18n } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dingtalk-links");
        setLinks(data);
      } catch (e) {
        // Supplementary widget, not a critical path - fail silently.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = showAll ? links : links.slice(0, 4);
  const hasMore = links.length > 4;

  return (
    <div className={`border border-border bg-card p-2.5 space-y-2 ${className || ""}`} data-testid="dingtalk-links-widget">
      <div className="flex items-center justify-between">
        <p className="font-mono-tag text-xs md:text-sm font-bold flex items-center gap-1.5">
          <Table size={15} /> {t("taskSidebar.dingtalkLinksTitle")}
        </p>
        <button data-testid="dingtalk-add-quick-button" onClick={() => setDialogOpen(true)} className="p-1 text-muted-foreground hover:text-primary" title={t("taskSidebar.addDingtalkLink")}>
          <Plus size={15} weight="bold" />
        </button>
      </div>
      {!loading && visible.length === 0 ? (
        <p className="text-xs font-bold text-muted-foreground py-6 text-center" data-testid="dingtalk-links-empty">
          {t("taskSidebar.dingtalkLinksEmpty")}
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="dingtalk-links-list">
          {visible.map((link) => (
            <div key={link.id} className="w-full flex items-center gap-2 border border-border bg-background px-2 py-1.5" data-testid={`dingtalk-link-item-${link.id}`}>
              <span className="w-7 h-7 flex items-center justify-center border border-green-600/30 bg-green-600/10 text-green-600 shrink-0">
                <Table size={13} />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-bold truncate">{link.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{t("taskSidebar.dingtalkUpdatedAt")} {formatCaptureDate(link.updated_at, i18n.language)}</p>
              </span>
              <button data-testid={`dingtalk-quick-edit-${link.id}`} onClick={() => setDialogOpen(true)} className="p-1 text-muted-foreground hover:text-primary shrink-0" title={t("taskSidebar.editDingtalkLink")}>
                <PencilSimple size={14} />
              </button>
              <button data-testid={`dingtalk-quick-open-${link.id}`} onClick={() => window.open(link.url, "_blank", "noopener")} className="p-1 text-muted-foreground hover:text-primary shrink-0" title={t("taskSidebar.openDingtalkLink")}>
                <ArrowSquareOut size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          data-testid="view-all-dingtalk-links-link"
          onClick={() => setShowAll((prev) => !prev)}
          className="flex items-center gap-1 text-xs md:text-sm font-bold text-primary hover:underline"
        >
          {showAll ? t("taskSidebar.collapseDingtalkLinks") : t("taskSidebar.viewAllDingtalkLinks")}
          <CaretDown size={12} weight="bold" className={showAll ? "rotate-180" : ""} />
        </button>
      )}

      <DingtalkLinksDialog open={dialogOpen} onOpenChange={setDialogOpen} links={links} onChange={setLinks} />
    </div>
  );
}

// Desktop-only sidebar for the Tasks page, laid out as three columns
// (mirrors the reference screenshot): today's timed-task timeline on the
// left (overdue tasks moved out to a dialog opened from the page header
// banner instead, per user request 2026-09-11), favorited notes + DingTalk
// sheet links in the middle, and a full-height Global Note scratchpad on
// the far right. Hidden below the `lg` breakpoint (mirrors SchedulePage's
// desktop-only unscheduled-tasks panel). `items-stretch` (the default)
// plus `DingtalkLinksCard`'s `flex-1` makes every column's visible bottom
// edge line up, mirroring the same technique used on SchedulePage.
export function TasksSidePanel({ tasks, onCreateScheduleItem, onOpenFavoriteCapture, favoritesRefreshKey, onOpenTaskDetail }) {
  return (
    <div className="hidden lg:flex gap-3 shrink-0 self-stretch" data-testid="tasks-side-panel">
      <div className="w-80 shrink-0 flex flex-col gap-3">
        <TodayScheduleCard tasks={tasks} onCreate={onCreateScheduleItem} onOpenDetail={onOpenTaskDetail} />
        <DingtalkLinksCard className="flex-1 flex flex-col min-h-0" />
      </div>
      <div className="w-96 shrink-0 flex flex-col gap-3">
        <FavoriteNotesCard onOpenCapture={onOpenFavoriteCapture} refreshKey={favoritesRefreshKey} />
        <GlobalNoteBox className="flex-1 min-h-0" />
      </div>
    </div>
  );
}
